<template lang="pug">
.order-history-slinger-x-item
  rogue-link.thumb(:to="linkPath" :productId="slingerXGroupProduct?.productId")
    StaticImage(:src="mainBundle?.thumbnail || slingerXGroupProduct?.thumbnail || null" :width="83" :alt="slingerXGroupProduct?.name || ''")
  .middle
    rogue-link.name(:to="linkPath" :productId="slingerXGroupProduct?.productId")
      h5 {{ slingerXGroupProduct?.name || '' }}
    .sku {{ slingerXGroupProduct?.sku }}
    hidden-bundle-config(v-if="hiddenSlingerXOptions.length" :bundle-options="hiddenSlingerXOptions")
  .slinger-x-addon(v-if="flatChildren.length")
    h5.included-items {{ __('Included Items') }}
    OrderItem(
      v-for="child in flatChildren"
      :key="child._id || child.productId"
      :buyRequest="child"
      theme="bundle"
    )
</template>
<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted } from 'vue'
import { useRogue } from '@/js/services/Rogue'
import { useProductStore } from '@/js/stores/productStore'
import { SLINGER_X_BUNDLE_SKUS } from '@/js/pages/product/rackbuilder/products/rack/functionalTrainer/FM/slinger-x/slingerXConstants'
import { getSlingerXAccessoryChildren, isSameSlingerXPurchase } from '@/js/pages/product/rackbuilder/products/rack/functionalTrainer/FM/slinger-x/slingerXUtils'
import type { BuyRequest } from '@/js/services/RogueTypes'

const HiddenBundleConfig = defineAsyncComponent(() => import('@/js/components/cart/hidden-bundle-config.vue'))

const props = defineProps<{
buyRequest: BuyRequest,
listItems: BuyRequest[],
}>()

const rogue        = useRogue()
const productStore = useProductStore()

const products = computed(() => productStore.products)

// groupedProductId + recipe (not groupedProductId alone) so two separate Slinger X purchases in the same order aren't conflated.
const siblingItems = computed(() =>
  props.listItems.filter(item => isSameSlingerXPurchase(item, props.buyRequest))
)

function resolveSku(item: BuyRequest) {
  return item.sku || products.value[item.productId]?.sku
}

// Only the main rack bundle is treated as the lead — mirrors Cave/MMS/MS, which never let a side/addon
// item substitute for a missing main unit (a bare side item is not a valid Slinger X purchase to display).
const mainBundle = computed(() =>
  siblingItems.value.find(item => SLINGER_X_BUNDLE_SKUS.includes(resolveSku(item))) ?? null
)


// Loaded by productId (not a hardcoded SKU) since Slinger X spans 4 group SKUs.
const slingerXGroupProduct = computed(() =>
  rogue.getProductById(props.buyRequest.metadata?.groupedProductId ?? props.buyRequest.productId)
)

// Mirrors order-history-mass-storage-item.vue's linkPath — group product's own url, plus ?rid= when a recipe is saved.
const linkPath = computed(() => {
  const recipe = mainBundle.value?.metadata?.recipe
  const baseLinkPath = slingerXGroupProduct.value?.url

  return recipe && baseLinkPath ? `${baseLinkPath}?rid=${recipe}` : baseLinkPath
})

// Rack:/Functional Trainer: config carriers are excluded — shown as an attribute summary instead.
const flatChildren = computed(() => siblingItems.value.flatMap(item => getSlingerXAccessoryChildren(rogue, item)))

// Rendered via hidden-bundle-config.vue, same mechanism as custom-order-item.vue's hiddenBundleOptions.
const hiddenSlingerXOptions = computed(() => {
  if (!mainBundle.value) return []

  const product = products.value[mainBundle.value.productId]
  if (product?.type !== 'bundle' || !product.bundleOptions || !mainBundle.value.children) return []

  return mainBundle.value.children
    .map(child => ({
      buyRequest : child,
      title      : product.bundleOptions?.find(opt => opt.id === child.metadata?.bundleOptionId)?.title || '',
      values     : product.bundleOptions?.find(opt => opt.id === child.metadata?.bundleOptionId)?.values,
    }))
    .filter(opt => ['Rack', 'Functional'].some(prefix => opt.title.startsWith(prefix)))
})

// Loads the Slinger X group product (for display name, productID, and SKU) + all siblings and their children
async function load() {
  const toLoad: number[] = [props.buyRequest.productId]

  siblingItems.value.forEach(item => {
    toLoad.push(item.productId)
    item.children?.forEach(child => {
      toLoad.push(child.productId)
      child.children?.forEach(grandchild => toLoad.push(grandchild.productId))
    })
  })

  const groupedId = props.buyRequest.metadata?.groupedProductId
  if (groupedId) {
    toLoad.push(groupedId)
  }

  await productStore.loadAll(toLoad.filter(id => !!id), rogue)
}

onMounted(() => { load() })
</script>
<style lang="scss" scoped>
$grid-left         : calc(65px + 16px);
$grid-left-desktop : calc(83px + 16px);

.order-history-slinger-x-item {
  // flex-grow: 1 ensures this element fills the .product column in order-detail.vue's flex row.
  // The parent delegator (order-history-custom-item.vue) is a fragment so .product class
  // attr inheritance does not reach here — this compensates for that.
  flex-grow: 1;
  display: grid;
  max-width: 100%;
  align-items: start;
  word-wrap: break-word;
  margin-top: 10px;
  margin-bottom: 10px;

  grid-template-columns: $grid-left minmax(100px, 1fr);
  grid-template-areas:
    "thumb middle"
    "addon addon";

  @include all-desktop() {
    grid-template-columns: $grid-left-desktop minmax(100px, 1fr);
    grid-template-areas:
      "thumb middle"
      ". addon";
  }
}

.thumb {
  grid-area: thumb;
  width: 65px;
  display: block;

  @include all-desktop() {
    width: 83px;
  }
}

.middle {
  grid-area: middle;
  padding: 0;
}

.name {
  color: $color-black;
  text-decoration: none;

  h5 {
    margin-bottom: 0;
    white-space: pre-wrap;
  }
}

.sku {
  font-size: $size-xxs;
  margin-top: 5px;
  color: #666;
  margin-bottom: $space-xxxs;
}

.slinger-x-addon {
  grid-area: addon;
  margin-top: $space-s;
}

.included-items {
  font-size: $size-xxs;
  text-transform: uppercase;
  padding-bottom: $space-xxs;
  border-bottom: 1px solid $color-gray-light;
  margin-bottom: $space-xs;
}
</style>
